import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  useHistory,
  useParams,
} from 'react-router-dom/cjs/react-router-dom.min';
import styled from 'styled-components';
import Loading from 'components/Loading';
import { POSModal, POSButton } from 'components/POS';
import Spacer from 'components/Spacer';
import Toaster from 'components/Toaster';
import { usePrintManager } from 'hooks/usePrintManager';
import { usePrintReceipt } from 'hooks/usePrintReceipt';
import { usePrintTickets } from 'hooks/usePrintTickets';
import { AppContext } from 'shared/AppContext';
import { useCart } from 'shared/CartContext';
import { getPrintSettings } from 'shared/utils/printUtils';
import { useCancelPOS } from './gql/queries/useCancelPOS';
import { useCheckoutPos } from './gql/queries/useCheckoutPOS';
import { usePOSRequest } from './gql/queries/usePOSRequest';
import { usePaymentSubscription } from './gql/subscriptions/payment-subscription';
import MobileDeliveryModal from './MobileDeliveryModal';
import { PrintingModal } from './PrintingModal';
import { CheckoutForm } from './StripeModal';

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: ${(props) => (props.error ? 'center' : 'space-between')};
  flex-grow: 1;
  gap: 16px;
  padding-top: ${(props) => (props.error ? '0' : '20px')};
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 20px;
`;

const StatusText = styled.p`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 18px;
  font-weight: 500;
  text-align: center;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.secondary || '#94A3B8'
      : '#64748B'};
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: auto;
`;

// Toast state management for external use
let showToastCallback = null;

export const setToastCallback = (callback) => {
  showToastCallback = callback;
};

export const successMessage = () => {
  if (showToastCallback) {
    showToastCallback({
      type: 'success',
      title: 'Payment Successful',
      message: 'Your transaction has been completed.',
    });
  }
};

export const errorMessage = () => {
  if (showToastCallback) {
    showToastCallback({
      type: 'error',
      title: 'Payment Failed',
      message: 'Please try again or use a different payment method.',
    });
  }
};

export const PaymentOption = ({
  isVisible,
  setIsVisible,
  manualCardPayment,
  accountIdPayment,
}) => {
  const history = useHistory();

  const {
    tickets,
    setPurchaseId,
    setConnectionStatus,
    event,
    isPrinting,
    setIsPrinting,
    account: eventAccount,
    setCurrentStatus,
  } = useCart();

  const IPAddress = localStorage.getItem('readerIp');

  const { id: eventId } = useParams();

  const [loading, setLoading] = useState(true);

  const [paymentError, setPaymentError] = useState(false);
  const { amount, accountId } = useCart();
  const [openSuccessModal, setOpenSuccessModal] = useState(false);

  const [isCheckoutVisible, setIsCheckoutVisible] = useState(false);

  const [paymentIntent, setPaymentIntent] = useState(null);

  // Toast state
  const [toastState, setToastState] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: '',
  });

  // Register toast callback on mount
  useEffect(() => {
    setToastCallback(({ type, title, message }) => {
      setToastState({
        isOpen: true,
        type,
        title,
        message,
      });
    });

    return () => {
      setToastCallback(null);
    };
  }, []);

  const closeToast = () => {
    setToastState((prev) => ({ ...prev, isOpen: false }));
  };

  const checkoutPos = useCheckoutPos();

  const { printReceipt } = usePrintReceipt();
  const { printTickets } = usePrintTickets();

  const urlParams = new URLSearchParams(window.location.search);
  const paymentIntentManual = urlParams.get('payment_intent');
  const redirectSuccess = urlParams.get('redirect_status') === 'succeeded';

  const {
    state: { account },
  } = useContext(AppContext);

  const availableAccount = account || eventAccount;

  const { printSettingEnabled, printReceiptEnabled, printReceiptWithTickets } =
    getPrintSettings({ event, availableAccount });

  const readerCookie = document.cookie
    .split(';')
    .find((cookie) => cookie.includes(`${accountId}-reader`))
    ?.replace(`${accountId}-reader=`, '')
    .trim();

  const { connect, isConnected } = usePrintManager();

  const print = async (transaction) => {
    try {
      await printTickets({ transaction, setIsPrinting });
    } catch (e) {
      console.error(e.toString());
    }
  };

  const printRec = async (transaction) => {
    try {
      await printReceipt({ transaction, setIsPrinting });
    } catch (e) {
      console.error(e.toString());
    }
  };

  usePaymentSubscription(paymentIntent, (data) => {
    setPaymentIntent(null);
    if (data && data.posPurchaseConfirmation.status) {
      onPurchaseConfirmation(data.posPurchaseConfirmation.payment_intent);
      setPaymentIntent(null);
    } else {
      if (isVisible) errorMessage();
      setLoading(false);
      if (isVisible) setPaymentError(true);
    }
  });

  const posRequest = usePOSRequest();
  const cancelPos = useCancelPOS();

  const replaceUrl = () => {
    const url = new URL(window.location.href);

    url.searchParams.delete('payment_intent');
    url.searchParams.delete('payment_intent_client_secret');
    url.searchParams.delete('redirect_status');

    history.replace(`${url.pathname}${url.search}`);
  };

  const printConfigOn =
    printSettingEnabled || printReceiptEnabled || printReceiptWithTickets;

  const printTicketAndReceipt = async (transaction) => {
    setCurrentStatus('Printing...');
    if (printSettingEnabled) {
      setIsPrinting(true);

      await print(transaction);

      if (printReceiptWithTickets) await printRec(transaction);
    }

    if (printReceiptEnabled) {
      setIsPrinting(true);

      await printRec(transaction);
    }

    setIsPrinting(false);
  };

  const onPurchaseConfirmation = async (paymentIntentManual) => {
    const seatsio = JSON.parse(sessionStorage.getItem('seatsio'));
    const holdToken = seatsio?.holdToken;

    const allTickets = Object.values(tickets).reduce((acc, value) => {
      const tickets = value.tickets?.map((ticket) => ({
        id: ticket.id,
        qty: ticket.count,
        ...(ticket.reserved_seat && ticket.count > 0
          ? {
              seats: ticket.seats?.map((seat) => ({
                seat_id: seat.id,
                ...(seat?.type ? { type: seat.type } : null),
              })),
            }
          : null),
      }));

      return [...acc, ...tickets];
    }, []);

    try {
      const result = await checkoutPos({
        event_id: +eventId,
        account_id: accountId,
        tickets: allTickets,
        hold_token: holdToken,
        payment_method: 'card',
        payment_intent: paymentIntentManual
          ? paymentIntentManual
          : paymentIntent,
      });

      if (result && result.data) {
        localStorage.removeItem('tickets');

        setPurchaseId(result.data?.checkoutPOS);
        setLoading(false);
        setIsVisible(false);

        if (printConfigOn && IPAddress) {
          setIsPrinting(true);
          const connectedPrinter = isConnected();
          if (!connectedPrinter && IPAddress) {
            setCurrentStatus('Connecting to printer...');
            try {
              const connected = await connect(
                IPAddress,
                setConnectionStatus,
                setIsVisible
              );
              if (connected) {
                printTicketAndReceipt(result?.data?.checkoutPOS);
              } else {
                console.error('Failed to connect to printer in payment option');
              }
            } catch (error) {
              console.error('Failed to connect to printer:', error);
            }
          } else if (connectedPrinter) {
            printTicketAndReceipt(result?.data?.checkoutPOS);
          }
        }

        replaceUrl();

        setOpenSuccessModal(true);
      } else {
        setPaymentError(true);
        errorMessage();
      }
    } catch (error) {
      if (manualCardPayment) history.goBack();
      setPaymentError(true);
      errorMessage();
    }
  };

  const makePosRequest = useCallback(async () => {
    if (isVisible && !manualCardPayment) {
      setLoading(true);
      setPaymentError(false);

      try {
        const result = await posRequest({
          account_id: accountId,
          amount: amount,
          reader_id: readerCookie,
        });

        if (result) {
          setPaymentIntent(result.data.posRequest);
        } else {
          setLoading(false);
          setPaymentError(true);
          errorMessage();
        }
      } catch (error) {
        setLoading(false);
        setPaymentError(true);
        if (!isCheckoutVisible && isVisible) errorMessage();
      }
    } else if (isVisible && manualCardPayment) {
      if (paymentIntentManual && redirectSuccess && accountIdPayment) {
        setLoading(true);

        onPurchaseConfirmation(paymentIntentManual);
      } else {
        setPaymentError(true);
        errorMessage();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, manualCardPayment, accountId, amount]);

  const cancelPosRequest = async () => {
    if (paymentIntent) {
      const result = await cancelPos({
        payment_intent: paymentIntent,
        reader_id: readerCookie,
      });

      return result.data.posCancel;
    }
  };

  useEffect(() => {
    if (isVisible) makePosRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const onClose = async () => {
    let result = !paymentIntent;
    if (paymentIntent) {
      result = await cancelPosRequest();
    }
    if (result) {
      setIsVisible(false);
    }
  };

  const handleManualCardPayment = async () => {
    let result = !paymentIntent;
    if (paymentIntent) {
      result = await cancelPosRequest();
    }
    if (result) {
      setIsVisible(false);
      setIsCheckoutVisible(true);
    }
  };

  return (
    <>
      <POSModal
        isVisible={isVisible}
        onClose={onClose}
        title="Tap, Swipe or Insert Card"
      >
        <BodyWrapper error={paymentError}>
          {loading && !paymentError && (
            <LoadingWrapper>
              <Loading type="spin" size={54} />
              <StatusText>Waiting for card...</StatusText>
            </LoadingWrapper>
          )}

          <ButtonWrapper>
            {(!manualCardPayment || paymentError) && (
              <POSButton
                variant="primary"
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  fontSize: '18px',
                }}
                onClick={handleManualCardPayment}
              >
                Key in Card Number
              </POSButton>
            )}

            {paymentError && !loading && (
              <>
                <Spacer size={4} />
                <POSButton
                  variant="primary"
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    fontSize: '18px',
                  }}
                  onClick={makePosRequest}
                >
                  Retry Transaction
                </POSButton>
              </>
            )}

            <Spacer size={4} />

            <POSButton
              variant="outlined"
              style={{ width: '100%', padding: '14px 20px', fontSize: '18px' }}
              onClick={onClose}
            >
              Cancel Transaction
            </POSButton>
          </ButtonWrapper>
        </BodyWrapper>
      </POSModal>

      <CheckoutForm
        isVisible={isCheckoutVisible}
        setIsVisible={setIsCheckoutVisible}
        amount={amount}
        accountId={accountId}
      />

      <MobileDeliveryModal
        isVisible={openSuccessModal}
        setIsVisible={setOpenSuccessModal}
        showSuccessMessage={true}
      />

      <PrintingModal
        isVisible={isPrinting}
        printSettingEnabled={printSettingEnabled}
        printReceiptEnabled={printReceiptEnabled}
        printReceiptWithTickets={printReceiptWithTickets}
        setOpenSuccessModal={setOpenSuccessModal}
      />

      <Toaster
        isOpen={toastState.isOpen}
        onClose={closeToast}
        type={toastState.type}
        title={toastState.title}
        message={toastState.message}
        duration={3000}
      />
    </>
  );
};
